import { useQuery } from '@tanstack/react-query';
import { ChevronDown, MapPin, Search } from 'lucide-react';
import { useState, type CSSProperties, type FC } from 'react';
import { Link, useNavigate } from 'react-router';

import type { StorefrontMenuItem } from '@/features/storefront/api/storefront';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { useContent } from '@/features/storefront/content/useContent';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { focalStyle } from '@/features/storefront/focal';
import { useCategoryPath, useShelfCategories } from '@/features/storefront/hooks/useShelfCategories';
import {
  HeaderBannerRail,
  HeaderCategoryLinks,
  MenuLink,
  MobileNav,
  StoreLogo,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import {
  CHROME_QUIET_SCROLL,
  menuItems,
  type HeaderProps,
} from '@/features/storefront/layouts/chrome/types';
import { HeaderLocationSelector } from '@/features/storefront/layouts/chrome/HeaderLocationSelector';
import { useStorefrontPaths } from '@/features/storefront/paths';
import { cn } from '@/lib/cn';
import { fixedImageSizes } from '@/lib/imageRenditions';

/**
 * The cobalt deck's ground, and the one thing in this file worth explaining.
 *
 * The shared announcement rail paints itself in the theme's *text* colour and
 * sets its copy in the theme's *background* colour, so it inverts correctly on
 * whatever palette a merchant lands on. This theme wants that inversion in the
 * brand cobalt rather than in near-black - the rail is the one place on the
 * whole shop where the primary is a ground rather than a mark - so rather than
 * fork the rail, the deck redefines the variable the rail reads: inside the
 * band the ink *is* the primary. Nothing outside the band is touched, and a
 * merchant who repaints their cobalt still repaints this rail with it, which
 * is the property that would be lost by hardcoding a blue here instead.
 */
const RAIL_STYLE = {
  '--storefront-text': 'var(--storefront-primary)',
} as CSSProperties;

/** The category bar's voice, worn by the merchant's menu and its stand-in alike. */
const HYPER_NAV = cn(
  'items-center gap-8 whitespace-nowrap text-[15px] font-semibold',
  CHROME_QUIET_SCROLL,
);

/**
 * Whether a navigation item is the shop's sale, and so wears the accent.
 *
 * The design paints exactly one link in the bar red, and it is always the one
 * pointing at the reductions. Read off the label rather than declared, because
 * the bar is the *merchant's* menu: they name their own clearance - "On Sale",
 * "Outlet", "Clearance" - and a theme that needed them to file it in a
 * particular slot would paint nothing at all for most shops. A shop with no
 * such link simply has a bar with no red on it, which is the honest result.
 */
function isSaleItem(item: StorefrontMenuItem): boolean {
  return /\b(sale|clearance|outlet|offers?)\b/i.test(item.label);
}

/**
 * One item on the category bar, and whatever the merchant filed under it.
 *
 * A furniture superstore is navigated by department and then by kind -
 * somebody wants a dining chair, not "chairs and stools" - so an item with
 * children opens them rather than making a shopper land on a department page
 * first. The item itself stays a link either way: the panel is a shortcut,
 * never the only way through.
 *
 * Opens on hover for a pointer and on focus for a keyboard, and closes on
 * Escape. No click handler on the trigger, because the trigger is the link.
 */
function NavItem({ item, preview }: { item: StorefrontMenuItem; preview?: boolean }) {
  const [open, setOpen] = useState(false);
  const children = item.items ?? [];
  // Set on the wrapper rather than passed to the link: `MenuLink` takes a
  // class and not a style, and the colour has to survive the three shapes it
  // renders in - a router link, a plain anchor, and an inert span in preview.
  const style = isSaleItem(item) ? { color: 'var(--storefront-accent)' } : undefined;

  if (children.length === 0) {
    return (
      <span style={style}>
        <MenuLink item={item} preview={preview} className="hover:opacity-60" />
      </span>
    );
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    >
      <span className="flex items-center gap-1.5" style={style}>
        <MenuLink item={item} preview={preview} className="hover:opacity-60" />
        <ChevronDown
          className={cn('size-3.5 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </span>

      {open ? (
        <div
          className="absolute start-0 top-full z-40 min-w-60 border py-2 shadow-xl"
          style={{
            backgroundColor: 'var(--storefront-background)',
            borderColor: 'var(--storefront-surface)',
            borderRadius: 'var(--storefront-radius)',
          }}
        >
          {children.map((child) => (
            <MenuLink
              key={`${child.label}:${child.url}`}
              item={child}
              preview={preview}
              className="block px-5 py-2 text-[15px] font-medium hover:opacity-60"
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The masthead's search box.
 *
 * A field rather than the shell's search *link*, because a superstore is
 * searched by name: somebody arrives knowing they want a bar stool at counter
 * height, and a trip through a landing page to find the box is a trip they
 * should not have to make. The address it navigates to is the search page's
 * own, so the term, the facets and the back button keep working exactly as
 * they do when a shopper gets there the shell's way.
 *
 * The "All Categories" block on the left is a label, not a control. The search
 * page's own facet rail is where a search is narrowed, and a second scope
 * picker in the masthead would be a second, worse copy of it - one that has to
 * be kept in step with a shop's shelves and disagrees with the rail the moment
 * it is not.
 *
 * Only mounted outside the customiser preview - it reaches for the router, and
 * the preview renders this chrome with none around it.
 */
function SearchField() {
  const paths = useStorefrontPaths();
  const navigate = useNavigate();
  const text = useContent('hyper');
  const [term, setTerm] = useState('');

  return (
    <form
      role="search"
      className="flex h-12 min-w-0 flex-1 items-center overflow-hidden ps-5 pe-1.5"
      style={{ backgroundColor: 'var(--storefront-surface)', borderRadius: '9999px' }}
      onSubmit={(event) => {
        event.preventDefault();
        const query = term.trim();
        if (query) navigate(paths.search(new URLSearchParams({ q: query })));
      }}
    >
      <span
        className="hidden shrink-0 items-center gap-1.5 border-e pe-4 text-sm font-semibold sm:flex"
        style={{ borderColor: 'var(--storefront-background)' }}
      >
        {text('header.search.scope')}
        <ChevronDown className="size-3.5" aria-hidden />
      </span>
      <input
        type="search"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        aria-label={text('header.search.label')}
        placeholder={text('header.search.placeholder')}
        className="h-full min-w-0 flex-1 bg-transparent px-4 text-sm outline-none"
        style={{ color: 'var(--storefront-text)' }}
      />
      <button
        type="submit"
        aria-label={text('header.search.submit')}
        className="grid size-9 shrink-0 place-items-center rounded-full"
        style={{
          backgroundColor: 'var(--storefront-background)',
          color: 'var(--storefront-text)',
        }}
      >
        <Search className="size-4" aria-hidden />
      </button>
    </form>
  );
}

/**
 * The showroom link in the shopper's corner.
 *
 * A shop selling sofas is asked "where can I sit on one" more than anything
 * else this corner could answer, and the design gives it the space. It is the
 * merchant's *own* page, though, not a hardcoded address: the link appears
 * only when they have actually published one, and wears whatever they titled
 * it. A permanent link to a page a shop has never written is a 404 in the
 * masthead of every storefront wearing this theme.
 *
 * Handles rather than titles, because a handle is what a shopper's address bar
 * carries and what a merchant is unlikely to change once it is linked.
 */
const STORE_PAGE_HANDLES = ['find-a-store', 'stores', 'showrooms', 'locations'];

function FindAStore() {
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const paths = useStorefrontPaths();
  const pages = useQuery({ queryKey: [...scope, 'pages'], queryFn: () => source.pages() });

  const page = (pages.data ?? []).find((entry) => STORE_PAGE_HANDLES.includes(entry.handle));
  if (!page) return null;

  return (
    <Link
      to={paths.page(page.handle)}
      className="hidden items-center gap-2 text-sm font-semibold hover:opacity-70 xl:flex"
    >
      <MapPin className="size-4" aria-hidden />
      {page.title}
    </Link>
  );
}

/** How many shelves stand in the circular rail before it stops. */
const SHELF_RAIL_LIMIT = 12;

/**
 * The rail of circular shelf tiles, and the fourth deck of this masthead.
 *
 * The single loudest thing about this theme: every top-level shelf as a
 * cut-out in a circle, scrolling sideways under the navigation, on every page
 * of the shop rather than only the home page. A superstore is chosen from
 * before it is searched, and this is the row that does the choosing.
 *
 * The picture fills the circle rather than fitting inside it - the one place
 * in this theme that departs from `STOREFRONT_TILE_IMAGE`. That rule exists so
 * a merchant's photograph is never cropped somewhere they did not choose, and
 * it is exactly right for a rectangular tile; a *circle* with a letterboxed
 * picture inside it is not a conservative crop, it is a broken circle. The
 * shelf tiles are square by construction, so nothing is lost to the fill.
 *
 * Renders nothing at all for a shop with no shelves yet, and is skipped in the
 * customiser preview, which has no storefront host resolved to ask.
 */
function ShelfRail({ preview }: { preview?: boolean }) {
  const shelves = useShelfCategories({ limit: SHELF_RAIL_LIMIT, enabled: !preview });
  const categoryPath = useCategoryPath();
  const text = useContent('hyper');

  if (preview || shelves.length === 0) return null;

  return (
    <div
      // Off a phone's inner pages while the app bar is on: its Categories tab
      // is the way to the shelves there (`index.css`).
      className="storefront-app-bar-rail border-t"
      style={{
        borderColor: 'var(--storefront-surface)',
        backgroundColor: 'var(--storefront-background)',
      }}
    >
      <StorefrontContainer>
        <nav
          aria-label={text('header.shelves')}
          className={cn('flex items-start gap-8 overflow-x-auto py-5', CHROME_QUIET_SCROLL)}
        >
          {shelves.map((shelf) => (
            <Link
              key={shelf.id}
              to={categoryPath(shelf.slug)}
              className="group flex w-24 shrink-0 flex-col items-center gap-2 text-center"
            >
              <span
                className="grid size-20 place-items-center overflow-hidden rounded-full transition-transform group-hover:scale-105"
                style={{ backgroundColor: 'var(--storefront-surface)' }}
              >
                {shelf.image_url ? (
                  <StorefrontImage
                    url={shelf.image_url}
                    sizes={fixedImageSizes(80)}
                    alt=""
                    className="size-full object-cover"
                    // The circle cuts the picture, so it is held at the
                    // merchant's chosen point; the span above clips a zoom.
                    style={focalStyle(shelf.image_focal)}
                  />
                ) : null}
              </span>
              <span className="text-[13px] font-semibold leading-tight">{shelf.name}</span>
            </Link>
          ))}
        </nav>
      </StorefrontContainer>
    </div>
  );
}

/**
 * A furniture superstore's masthead, in four decks.
 *
 * The cobalt rail carries the standing promises a big shop leads with - free
 * express shipping, the trial window, how long a made-to-order piece takes -
 * because those are half of why somebody buys a sofa from one shop rather than
 * another. The white deck under it is the shop itself: the wordmark, the
 * search box across the middle, and the shopper's corner on the right. The
 * category bar carries the merchant's own navigation, with whatever they filed
 * under each item opening beneath it and their clearance link in the sale red.
 * And the fourth deck is the rail of circular shelf tiles, which is the thing
 * this theme is drawn around.
 *
 * Four decks is a tall header, and `headerHeight` in `meta.ts` says so
 * honestly - a full-height banner subtracts all of it.
 *
 * Like Serra, Voltic and Tools & Machine, the bar stands the shop's own
 * shelves in when the merchant has built no menu: a superstore header with
 * nothing in it is not a quieter header, it is a shop with no way to the
 * furniture.
 */
export const Header: FC<HeaderProps> = ({
  storeName,
  logoUrl,
  description,
  header,
  menu,
  endSlot,
  preview,
}) => {
  const items = menuItems(menu);
  const text = useContent('hyper');
  // With no menu built the bar stands the shelves in, and the shelves query is
  // skipped in preview - so in preview an empty menu means no bar at all.
  const hasBar = items.length > 0 || !preview;

  return (
    <header
      className={cn(header.sticky && !preview && 'sticky top-0 z-40')}
      style={{ backgroundColor: 'var(--storefront-background)' }}
    >
      {/* This family is drawn around a rail, so with no banners written it
          keeps carrying the shop's own line rather than closing up. */}
      <div style={RAIL_STYLE}>
        <HeaderBannerRail header={header} fallback={description} preview={preview} />
      </div>

      {/* A phone's row is menu, mark, name and the corner icons with no room
          to spare, so the gaps and the wordmark come down below `sm` - at
          the desktop sizes a short name like "AgroNest" kept two letters. */}
      <StorefrontContainer className="flex min-h-[4.75rem] items-center gap-3 py-3.5 sm:gap-6">
        <MobileNav menu={menu} hiddenFrom="lg:hidden" preview={preview} categoryTree />
        <StoreLogo
          storeName={storeName}
          logoUrl={logoUrl}
          logoWidthPx={header.logo_width_px}
          showStoreName={header.show_store_name}
          preview={preview}
          className="gap-2 sm:gap-3"
          nameClassName="text-lg uppercase tracking-[-0.01em] sm:text-[1.75rem]"
          nameStyle={{ fontFamily: 'var(--storefront-heading-font)', fontWeight: 800 }}
        />
        <HeaderLocationSelector header={header} preview={preview} placement="inline" />

        {/* The box takes the middle of the deck from the large breakpoint up
            and stands down below it, where the wordmark and the corner have
            the row to themselves and the sheet carries the navigation. */}
        <div className="hidden min-w-0 flex-1 justify-center lg:flex">
          {preview ? (
            <div
              aria-hidden
              className="flex h-12 min-w-0 flex-1 items-center px-5 text-sm"
              style={{
                backgroundColor: 'var(--storefront-surface)',
                borderRadius: '9999px',
                color: 'var(--storefront-muted)',
              }}
            >
              {text('header.search.placeholder')}
            </div>
          ) : (
            <SearchField />
          )}
        </div>

        <div className="ms-auto flex shrink-0 items-center gap-2.5 sm:gap-5">
          {/* Skipped in the customiser preview, which has no storefront host
              resolved and would ask the wrong shop for its pages. */}
          {preview ? null : <FindAStore />}
          {endSlot}
        </div>
      </StorefrontContainer>

      {hasBar ? (
        <div
          className="hidden border-t lg:block"
          style={{
            borderColor: 'var(--storefront-surface)',
            backgroundColor: 'var(--storefront-background)',
          }}
        >
          <StorefrontContainer>
            {items.length > 0 ? (
              <nav
                aria-label={menu?.name}
                className={cn('flex overflow-x-auto py-3.5', HYPER_NAV)}
              >
                {items.map((item) => (
                  <NavItem key={`${item.label}:${item.url}`} item={item} preview={preview} />
                ))}
              </nav>
            ) : (
              /* No menu built yet. Seven, because this bar is a row of its own
                 with the widest page column in the set to spend. Skipped in
                 preview, where no storefront host is resolved and the query
                 would ask about the wrong shop. */
              <HeaderCategoryLinks
                className={cn('flex overflow-x-auto py-3.5', HYPER_NAV)}
                linkClassName="hover:opacity-60"
                header={header}
                limit={7}
              />
            )}
          </StorefrontContainer>
        </div>
      ) : null}

      <ShelfRail preview={preview} />
    </header>
  );
};
